// src/layouts/handover/HandoverSheetTab.js
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { TextField, useTheme, useMediaQuery } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import useHandOversheetData from "./handoverheetData";
import LoadingScreen from "layouts/loading/loadingscreen";
import api from "api/api";
import Swal from "sweetalert2";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import FileSaver from "file-saver";
import accountLabelSx from "utils/accountLabelSx";

export default function HandoverSheetTab() {
  // ✅ localStorage account_id로 거래처 고정 + 셀렉트 필터링
  const localAccountId = useMemo(() => localStorage.getItem("account_id") || "", []);
  const isAccountLocked = !!localAccountId;

  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [selectedAccountId, setSelectedAccountId] = useState(() => localAccountId || "");
  // 조회 시작 전/후 깜빡임 방지를 위한 화면 로딩 상태
  const [viewLoading, setViewLoading] = useState(true);
  const loadingStartedRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { handOverListRows, accountList, loading, fetcHandOverList } =
    useHandOversheetData(selectedAccountId);

  // ✅ localStorage account_id 기준으로 거래처 리스트 필터링
  const filteredAccountList = useMemo(() => {
    if (!localAccountId) return accountList || [];
    return (accountList || []).filter((row) => String(row.account_id) === String(localAccountId));
  }, [accountList, localAccountId]);

  // 거래처 변경 시 로딩을 먼저 시작하고 조회를 트리거
  const beginAccountLoading = () => {
    setViewLoading(true);
    loadingStartedRef.current = false;
  };

  const setSelectedAccountWithLoading = (nextAccountId) => {
    const nextId = String(nextAccountId ?? "");
    if (nextId === String(selectedAccountId ?? "")) return;
    beginAccountLoading();
    setSelectedAccountId(nextId);
  };

  const onSearchList = (e) => setSelectedAccountWithLoading(e.target.value);

  // ✅ accountList 로딩 후 selectedAccountId 결정
  // - localStorage account_id가 있으면 무조건 그걸로 고정
  // - 없으면: 첫번째 거래처 자동 선택
  useEffect(() => {
    if (!accountList || accountList.length === 0) return;

    if (localAccountId) {
      setSelectedAccountWithLoading(localAccountId);
      return;
    }

    if (!selectedAccountId) {
      setSelectedAccountWithLoading(accountList[0].account_id);
    }
  }, [accountList, selectedAccountId, localAccountId]);

  useEffect(() => {
    if (selectedAccountId) {
      fetcHandOverList(selectedAccountId);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId) setForm((prev) => ({ ...prev, account_id: selectedAccountId }));
  }, [selectedAccountId]);

  useLayoutEffect(() => {
    if (handOverListRows && handOverListRows.length > 0) {
      setForm(handOverListRows[0] || {});
      setOriginalForm(handOverListRows[0] || {});
    } else {
      setForm({});
      setOriginalForm({});
    }
    // 조회 결과가 화면 상태에 반영된 뒤 로딩 종료
    if (selectedAccountId) {
      setViewLoading(false);
    }
  }, [handOverListRows, selectedAccountId]);

  useEffect(() => {
    if (!viewLoading) return;

    if (loading) {
      loadingStartedRef.current = true;
      return;
    }

    if (loadingStartedRef.current) {
      setViewLoading(false);
      loadingStartedRef.current = false;
    }
  }, [loading, viewLoading]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      account_id: selectedAccountId,
      user_id: localStorage.getItem("user_id"),
    };

    try {
      const response = await api.post("/Operate/HandOverSave", payload);
      if (response.data.code === 200) {
        Swal.fire({
          title: "저장",
          text: "저장되었습니다.",
          icon: "success",
          confirmButtonColor: "#d33",
          confirmButtonText: "확인",
        });
        beginAccountLoading();
        await fetcHandOverList(selectedAccountId);
      }
    } catch (err) {
      Swal.fire({
        title: "실패",
        text: err.message || "저장 중 오류 발생",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "확인",
      });
    }
  };

  if (loading || viewLoading) return <LoadingScreen />;

  const normalize = (v) => (v === null || v === undefined ? "" : String(v).trim());
  const isChanged = (key) => normalize(form[key]) !== normalize(originalForm[key]);

  // ✅ 셀 렌더 함수 (모바일 폰트/패딩 반영)
  const renderInput = (key, opts = {}) => {
    const changed = isChanged(key);
    const color = changed ? "red" : "black";

    return (
      <TextField
        {...opts}
        fullWidth
        value={form[key] ?? ""}
        onChange={handleChange(key)}
        inputProps={{
          ...(opts.inputProps || {}),
          style: {
            ...(opts.inputProps?.style || {}),
            color,
            fontSize: isMobile ? 10 : 12,
            padding: isMobile ? "2px 4px" : "4px 6px",
          },
        }}
        sx={{
          "& .MuiInputBase-input": {
            color,
            fontSize: isMobile ? 10 : 12,
            padding: isMobile ? "2px 4px" : "4px 6px",
          },
          "& .MuiInputBase-inputMultiline": {
            color,
            fontSize: isMobile ? 10 : 12,
          },
          "& input[type='date']": {
            color,
            fontSize: isMobile ? 10 : 12,
          },
        }}
      />
    );
  };

  const handleExcelDownload = async () => {
    // ✅ form 값 정리 (제어문자 제거)
    const sanitize = (v) =>
      typeof v === "string" ? v.replace(/[\x00-\x1F\x7F]/g, "").trim() : v ?? "";

    const cleanedForm = { ...form };
    Object.keys(cleanedForm).forEach((k) => (cleanedForm[k] = sanitize(cleanedForm[k])));

    const workbook = await XlsxPopulate.fromBlankAsync();
    const sheet = workbook.sheet(0);
    sheet.name("Sheet1");

    const gray = "F0F0F0";
    const fontKor = "맑은 고딕";
    const borderThin = {
      top: { style: "thin", color: "999999" },
      left: { style: "thin", color: "999999" },
      bottom: { style: "thin", color: "999999" },
      right: { style: "thin", color: "999999" },
    };

    ["A", "B", "C", "D", "E", "F"].forEach((col, i) => {
      sheet.column(col).width(i % 2 === 0 ? 18 : 25);
    });

    // 1. 제목
    sheet.range("A1:F1").merged(true);
    sheet.range("A1:F1").style({ border: borderThin });
    sheet.cell("A1").value("업 무 인 수 인 계 서").style({
      bold: true,
      fontSize: 12,
      fontFamily: fontKor,
      horizontalAlignment: "center",
      verticalAlignment: "center",
      fill: gray,
    });
    sheet.row(1).height(25);

    // 2. 인계자 / 인수자 / 확인자
    const headerData = [
      ["A2:B2", "인계자"],
      ["C2:D2", "인수자"],
      ["E2:F2", "확인자"],
    ];

    headerData.forEach(([range, title]) => {
      sheet.range(range).merged(true);
      sheet.range(range).style({ border: borderThin });
      sheet.cell(range.split(":")[0]).value(title).style({
        bold: true,
        fontFamily: fontKor,
        horizontalAlignment: "center",
        verticalAlignment: "center",
        fill: gray,
      });
    });

    // 3. 기본 정보
    const baseRows = [
      [
        "업체명",
        cleanedForm.handover_company,
        "업체명",
        cleanedForm.acquisition_company,
        "부서명",
        cleanedForm.check_team,
      ],
      [
        "성명",
        cleanedForm.handover_name,
        "성명",
        cleanedForm.acquisition_name,
        "성명",
        cleanedForm.check_name,
      ],
      [
        "인계일자",
        cleanedForm.handover_dt,
        "인수일자",
        cleanedForm.acquisition_dt,
        "인계장소",
        cleanedForm.handover_location,
      ],
    ];

    let rowIdx = 3;
    baseRows.forEach((cols) => {
      ["A", "C", "E"].forEach((col, i) => {
        sheet
          .cell(`${col}${rowIdx}`)
          .value(cols[i * 2])
          .style({
            bold: true,
            fill: gray,
            fontFamily: fontKor,
            verticalAlignment: "center",
            horizontalAlignment: "center",
            border: borderThin,
          });
      });

      ["B", "D", "F"].forEach((col, i) => {
        sheet
          .cell(`${col}${rowIdx}`)
          .value(cols[i * 2 + 1])
          .style({
            fontFamily: fontKor,
            verticalAlignment: "center",
            horizontalAlignment: "left",
            border: borderThin,
          });
      });

      rowIdx++;
    });

    // 4. 섹션 타이틀
    const addSection = (title) => {
      rowIdx++;
      sheet.range(`A${rowIdx}:F${rowIdx}`).merged(true);
      sheet.range(`A${rowIdx}:F${rowIdx}`).style({ border: borderThin });
      sheet.cell(`A${rowIdx}`).value(title).style({
        bold: true,
        fontFamily: fontKor,
        fill: gray,
        horizontalAlignment: "left",
        verticalAlignment: "center",
      });
      sheet.row(rowIdx).height(22);
    };

    // 5. 라벨 + 값 블록
    const addRowBlock = (label, value, height = 20) => {
      rowIdx++;

      sheet.cell(`A${rowIdx}`).value(label).style({
        bold: true,
        fill: gray,
        fontFamily: fontKor,
        verticalAlignment: "center",
        horizontalAlignment: "left",
        border: borderThin,
      });

      sheet.range(`B${rowIdx}:F${rowIdx}`).merged(true);
      sheet.range(`B${rowIdx}:F${rowIdx}`).style({ border: borderThin });
      sheet
        .cell(`B${rowIdx}`)
        .value(value || "")
        .style({
          fontFamily: fontKor,
          verticalAlignment: "center",
          horizontalAlignment: "left",
        });

      sheet.row(rowIdx).height(height);
    };

    // 6. 내용 채우기
    addSection("1. 업무 현황");
    addRowBlock("▶ 급식 대상자 수", cleanedForm.meal_number);
    addRowBlock("▶ 급식 운영 시간", cleanedForm.meal_time);
    addRowBlock("▶ 식수 운영 방식 및 배식방식", cleanedForm.catering_ration);
    addRowBlock("▶ 일일 식단 운영 방식", cleanedForm.normal_diet);
    addRowBlock("▶ 주방 인력 구성 (이름, 연락처)", cleanedForm.kitchen_member);
    addRowBlock("▶ 근무 시간 및 스케줄 근무 형태", cleanedForm.work_type);
    addRowBlock("▶ 발주 프로그램 아이디 & 비밀번호", cleanedForm.order_program_info);
    addRowBlock("▶ 식재료 발주 방식 및 1인량", cleanedForm.ingredients_order_type);
    addRowBlock("▶ 검수 및 입고 방법", cleanedForm.inspection_store_type);
    addRowBlock("▶ 재고 관리 방법", cleanedForm.stock_manage_type);

    addSection("2. 주요 업무 및 주의사항");
    addRowBlock("▶ 식단 작성 및 제출 주기", cleanedForm.diet_submit_cycle);
    addRowBlock("▶ 위생 점검 사항", cleanedForm.hygiene_note);
    addRowBlock("▶ 주요 문서", cleanedForm.hot_issue);
    addRowBlock("▶ 요양원) 케어포 등 프로그램 사용 여부", cleanedForm.program_use_whether);
    addRowBlock("▶ 라운딩 횟수 및 만족도 조사 여부", cleanedForm.rounding_satis_whether);
    addRowBlock("▶ 클레임 및 민원 이력", cleanedForm.complain_note);
    addRowBlock("▶ 대내외 연락처", cleanedForm.manager_phone);
    addRowBlock("▶ 쓰레기 처리 방식", cleanedForm.trash_treatment_type);
    addRowBlock("▶ 정기 일정", cleanedForm.regular_schedule);

    addSection("3. 인계자 요청사항 및 기타 특이사항");
    addRowBlock("내용", cleanedForm.special_note, 60);

    const blob = await workbook.outputAsync();

    const selectedAccount = (accountList || []).find((a) => a.account_id === selectedAccountId);
    const accountName = selectedAccount ? selectedAccount.account_name : "handover";

    FileSaver.saveAs(blob, `${accountName}_인수인계서.xlsx`);
  };

  // ✅ 셀 스타일 — 타이틀 테이블/본문 테이블 공통
  const sharedCellSx = {
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      minWidth: isMobile ? 700 : 900,
    },
    "& th, & td": {
      border: "1px solid #000",
      fontSize: isMobile ? "10px" : "12px",
      textAlign: "left",
      verticalAlign: "middle",
      padding: isMobile ? "2px 4px" : "3px 6px",
    },
    "& th": {
      backgroundColor: "#f0f0f0",
      fontWeight: "bold",
      textAlign: "center",
    },
  };

  // ✅ 타이틀+본문을 하나의 스크롤 박스에 같이 넣어서 스크롤바가 타이틀 옆까지 이어지게 함.
  //    타이틀은 그 안에서 position:sticky를 "테이블 셀"이 아니라 감싸는 div(MDBox)에 줘서 고정
  //    — sticky를 th/thead에 직접 주면 스크롤 중 테두리가 떨리는 브라우저 버그가 있어 회피함
  const scrollBoxSx = {
    ...sharedCellSx,
    flex: 1,
    mt: 1,
    maxHeight: isMobile ? "55vh" : "75vh",
    overflowX: "auto",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  };

  // ✅ 타이틀 테이블을 감싸는 div — 여기에 sticky를 줌(테이블 셀에는 안 줌)
  const stickyTitleWrapSx = {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: "#ffffff",
    // ✅ 타이틀 테이블 아래 테두리가 본문 테이블 위 테두리와 겹쳐 두 배로 굵어 보이는 것 방지
    "& th": {
      borderBottom: "none",
    },
  };

  return (
    <>
      {/* 상단 필터 + 버튼 바 (모바일 대응) */}
      <MDBox
        pt={1}
        gap={1}
        pb={1}
        sx={{
          display: "flex",
          justifyContent: isMobile ? "space-between" : "flex-end",
          alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
          columnGap: isMobile ? 1 : 2,
          rowGap: isMobile ? 1 : 0,
          position: "sticky",
          zIndex: 10,
          top: 78,
          backgroundColor: "#ffffff",
        }}
      >
        {(filteredAccountList || []).length > 0 && (
          <TextField
            select
            size="small"
            label={isAccountLocked ? "거래처(고정)" : "거래처"}
            value={selectedAccountId}
            onChange={onSearchList}
            sx={{
              minWidth: isMobile ? 160 : 200,
              fontSize: isMobile ? "12px" : "14px",
              ...accountLabelSx,
            }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            disabled={isAccountLocked} // ✅ localStorage로 고정이면 변경 불가 (원하면 제거)
          >
            {(filteredAccountList || []).map((row) => (
              <option key={row.account_id} value={row.account_id}>
                {row.account_name}
              </option>
            ))}
          </TextField>
        )}

        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          sx={{
            fontSize: isMobile ? "11px" : "13px",
            minWidth: isMobile ? 90 : 110,
          }}
        >
          저장
        </MDButton>

        <MDButton
          variant="gradient"
          color="success"
          onClick={handleExcelDownload}
          sx={{
            fontSize: isMobile ? "11px" : "13px",
            minWidth: isMobile ? 110 : 130,
          }}
        >
          엑셀 다운로드
        </MDButton>
      </MDBox>

      {/* 타이틀+본문을 한 스크롤 박스 안에 같이 둬서 스크롤바가 타이틀 옆까지 이어지게 함 */}
      <MDBox pb={3} sx={scrollBoxSx}>
        {/* 타이틀 — 이 박스 안에서 div(sticky)로 고정, 테이블 셀에는 sticky를 안 줌 */}
        <MDBox sx={stickyTitleWrapSx}>
          <table>
            <tbody>
              <tr>
                <th colSpan={6}>업 무 인 수 인 계 서</th>
              </tr>
            </tbody>
          </table>
        </MDBox>

        <table>
          <tbody>
            <tr>
              <th colSpan={2}>인계자</th>
              <th colSpan={2}>인수자</th>
              <th colSpan={2}>확인자</th>
            </tr>
            <tr>
              <td>업체명</td>
              <td>{renderInput("handover_company")}</td>
              <td>업체명</td>
              <td>{renderInput("acquisition_company")}</td>
              <td>부서명</td>
              <td>{renderInput("check_team")}</td>
            </tr>
            <tr>
              <td>성명</td>
              <td>{renderInput("handover_name")}</td>
              <td>성명</td>
              <td>{renderInput("acquisition_name")}</td>
              <td>성명</td>
              <td>{renderInput("check_name")}</td>
            </tr>
            <tr>
              <td>인계일자</td>
              <td>{renderInput("handover_dt", { type: "date" })}</td>
              <td>인수일자</td>
              <td>{renderInput("acquisition_dt", { type: "date" })}</td>
              <td>인계장소</td>
              <td>{renderInput("handover_location")}</td>
            </tr>

            {/* 1. 업무 현황 */}
            <tr>
              <th colSpan={6}>1. 업무 현황</th>
            </tr>
            <tr>
              <td>▶ 급식 대상자 수</td>
              <td colSpan={5}>{renderInput("meal_number")}</td>
            </tr>
            <tr>
              <td>▶ 급식 운영 시간
                <br />
                (예 : 조식: 7:15, 중식: 11:15, 석식:16:30 등 / 주간보호 업장일 시 함께 작성 필요)
              </td>
            </tr>
            <tr>
              <td>
                ▶ 식수 운영 방식 및 배식 방식 <br />
                <span style={{ color: "red", fontSize: isMobile ? "9px" : "11px" }}>
                  (예 : 선택식, 일품식 / 바트배식, 식판배식, 그릇배식 등) <br />* 그 외 셀프바 및
                  간편식 제공 시 어떤식으로 나가는지 기재 필요
                </span>
              </td>
              <td colSpan={5}>{renderInput("catering_ration", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 일일 식단 운영 방식
                <br />
                (예 : 1일 3식, 간식 포함 여부, 식사제공시간, 생신잔치 유무 등)
              </td>
              <td colSpan={5}>{renderInput("normal_diet", { multiline: true })}</td>
            </tr>
            <tr>
              <td>▶ 주방 인력 구성 (이름, 연락처)</td>
              <td colSpan={5}>{renderInput("kitchen_member", { multiline: true })}</td>
            </tr>
            <tr>
              <td>▶ 근무 시간 및 스케줄 근무 형태</td>
              <td colSpan={5}>{renderInput("work_type", { multiline: true })}</td>
            </tr>
            <tr>
              <td>▶ 발주 프로그램 아이디 & 비밀번호</td>
              <td colSpan={5}>{renderInput("order_program_info")}</td>
            </tr>
            <tr>
              <td>
                ▶ 식재료 발주 방식 및 1인량
                <br />
                (업체명, 발주 요일, 발주 방법 등)
              </td>
              <td colSpan={5}>{renderInput("ingredients_order_type", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 검수 및 입고 방법
                <br />
                (시간, 장소, 확인자 등)
              </td>
              <td colSpan={5}>{renderInput("inspection_store_type", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 재고 관리 방법
                <br />
                (보관방식, 소비기한 관리 등)
              </td>
              <td colSpan={5}>{renderInput("stock_manage_type", { multiline: true })}</td>
            </tr>

            {/* 2. 주요 업무 및 주의사항 */}
            <tr>
              <th colSpan={6}>2. 주요 업무 및 주의사항</th>
            </tr>
            <tr>
              <td>
                ▶ 식단 작성 및 제출 주기
                <br />
                (제출부서, 제출일정 포함)
              </td>
              <td colSpan={5}>{renderInput("diet_submit_cycle", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 위생 점검 사항
                <br />
                (정기/수시 점검 내용 및 주의사항)
              </td>
              <td colSpan={5}>{renderInput("hygiene_note", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 주요 문서
                <br />
                (일일 급식일지, 위생점검표, 주방도구 소독일지, 월별 교육, 조리지시서 등)
              </td>
              <td colSpan={5}>{renderInput("hot_issue", { multiline: true })}</td>
            </tr>
            <tr>
              <td>▶ 요양원) 케어포 등 프로그램 사용 여부</td>
              <td colSpan={5}>{renderInput("program_use_whether")}</td>
            </tr>
            <tr>
              <td>▶ 라운딩 횟수 및 만족도 조사 여부</td>
              <td colSpan={5}>{renderInput("rounding_satis_whether")}</td>
            </tr>
            <tr>
              <td>
                ▶ 클레임 및 민원 이력
                <br />
                (최근 3개월 내)
              </td>
              <td colSpan={5}>{renderInput("complain_note", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 대내외 연락처
                <br />
                (식재업체, 점검담당자, 긴급파출업체 등 주요 연락처)
              </td>
              <td colSpan={5}>{renderInput("manager_phone", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 쓰레기 처리 방식
                <br />
                (업체수거 / 쓰레기봉투구매 등)
              </td>
              <td colSpan={5}>{renderInput("trash_treatment_type", { multiline: true })}</td>
            </tr>
            <tr>
              <td>
                ▶ 정기 일정
                <br />
                (예: 주기적 교육, 회의, 설비점검 등)
              </td>
              <td colSpan={5}>{renderInput("regular_schedule", { multiline: true })}</td>
            </tr>

            {/* 3. 특이사항 */}
            <tr>
              <th colSpan={6}>3. 인계자 요청사항 및 기타 특이사항</th>
            </tr>
            <tr>
              <td>내용</td>
              <td colSpan={5}>{renderInput("special_note", { multiline: true, rows: 5 })}</td>
            </tr>
          </tbody>
        </table>
      </MDBox>
    </>
  );
}
